import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { SegmentoVisao } from '@/contexts/SegmentoVisaoContext';

/** As etapas vêm do backend, que as tira da fonte única (situacao-esim.ts). */
export type EtapaValidacao =
  | 'nao_iniciada' | 'esperando_cliente' | 'pediu_ajuda' | 'verificacao_reenviada' | 'envio_revisao'
  | 'sem_foto' | 'conferir_iccid' | 'troca' | 'validado' | 'outro';

export type TipoLinhaEsim = 'esim' | 'simcard';

export interface GrupoValidacao {
  consultorId: string | null;
  consultorNome: string | null;
  fotoUrl: string | null;
  equipeId: string | null;
  equipeNome: string | null;
  tipo: TipoLinhaEsim;
  etapa: EtapaValidacao;
  linhas: number;
}

/** OS 8 STATUS (Douglas 24/09/2026): o nome diz o fato, nunca uma ordem. */
export const ROTULO_ETAPA: Record<EtapaValidacao, string> = {
  nao_iniciada: 'Não iniciada',
  esperando_cliente: 'Verificação enviada',
  pediu_ajuda: 'Ajudando',
  verificacao_reenviada: 'Verificação reenviada',
  sem_foto: 'Esperando foto do chip',
  conferir_iccid: 'Conferindo chip',
  troca: 'Trocando chip',
  envio_revisao: 'Erro no envio',
  validado: 'Validado',
  outro: 'Outro',
};

/** O guia da triagem: uma frase por status. */
export const GUIA_ETAPA: Partial<Record<EtapaValidacao, string>> = {
  nao_iniciada: 'ninguém mandou nada ainda',
  esperando_cliente: 'perguntamos se funciona; ele ainda não respondeu',
  pediu_ajuda: 'digitou 1 ou escreveu pedindo ajuda; a Qualidade atende',
  verificacao_reenviada: 'já foi ajudado; perguntamos de novo e esperamos o 2',
  sem_foto: 'pedimos a foto do chip físico',
  conferir_iccid: 'a foto chegou; falta conferir o número do chip',
  troca: 'a troca foi pedida à gerente da Claro',
  envio_revisao: 'a mensagem não saiu; mande de novo',
  validado: 'o cliente digitou 2; segue para Boas-vindas',
};

/** A régua de cada tipo: o eSIM é pergunta e resposta; o SIMCARD tem a foto
 *  do chip e a troca com a gerente no meio (Douglas 23/09/2026). */
export const ETAPAS_DO_TIPO: Record<'todos' | TipoLinhaEsim, EtapaValidacao[]> = {
  todos: ['nao_iniciada', 'esperando_cliente', 'pediu_ajuda', 'verificacao_reenviada', 'sem_foto', 'conferir_iccid', 'troca', 'envio_revisao', 'validado'],
  esim: ['nao_iniciada', 'esperando_cliente', 'pediu_ajuda', 'verificacao_reenviada', 'envio_revisao', 'validado'],
  simcard: ['sem_foto', 'conferir_iccid', 'troca', 'pediu_ajuda', 'verificacao_reenviada', 'envio_revisao', 'validado'],
};

/** A MESMA consulta da aba Validação eSIM — a Triagem reusa (e divide o cache). */
export const chaveValidacaoEsimVisao = (mes: string, tipoPessoa: SegmentoVisao) =>
  ['visao-prime-validacao-esim', mes.substring(0, 7), tipoPessoa] as const;

export async function buscarValidacaoEsimVisao(mesReferencia: string, tipoPessoa: SegmentoVisao): Promise<GrupoValidacao[]> {
  const mes = mesReferencia.substring(0, 7);
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sua sessão expirou. Entre novamente.');
  const base = ((import.meta.env.VITE_SUPABASE_URL as string) || 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');
  const r = await fetch(`${base}/v1/qualidade/ativacao/visao-prime?mes=${mes}&tipoPessoa=${tipoPessoa}`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  const corpo = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(corpo?.error || 'Não foi possível carregar a validação do eSIM.');
  return (corpo.data || []) as GrupoValidacao[];
}

export function useValidacaoEsimVisao(mesReferencia: string, tipoPessoa: SegmentoVisao) {
  return useQuery({
    queryKey: chaveValidacaoEsimVisao(mesReferencia, tipoPessoa),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    queryFn: () => buscarValidacaoEsimVisao(mesReferencia, tipoPessoa),
  });
}
